import { addNewUserValidations } from 'src/views/pages/manageUser/NewUser/Body/Form/validations';
import { ComboBox } from 'src/components/ui/combobox';
import { DealerManagementService } from 'src/services/dealer-management.service';
import Input from 'src/components/forms/input';
import { IUser } from 'src/utils/interfaces/user';
import { MODAL_CONTROL_ACTIONS } from 'src/utils/enums/user-store-actions.enum';
import { NUMBER_ZERO } from 'src/utils/constants';
import { Permissions } from 'src/utils/enums/permissions.enum';
import Spinner from 'src/components/ui/spinner';
import { useFormik } from 'formik';
import { useLocalization } from 'src/utils/useLocalization';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    addHyphens,
    onKeyPressPhoneHandler,
} from 'src/views/pages/manageUser/NewUser/Body/Form/Form';
import {
    ISM_ADD_NEW_USER_MODAL_CANCEL,
    ISM_ADD_NEW_USER_MODAL_CONFIRM,
    ISM_ADD_NEW_USER_MODAL_CONTENT,
    ISM_ADD_NEW_USER_MODAL_TITLE,
    SYSTEM_ADMIN_PAGE_COUNTRY_LABEL,
} from 'src/localization/resouceKeys';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import './styles.scss';

interface IISMAddNewUserProps {
    prefillEmail?: string;
}

interface IPermissionCheckbox {
    label: string;
    value: Permissions;
}

const ISMAddNewUser: React.FC<IISMAddNewUserProps> = ({
    prefillEmail = '',
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();
    const { ismAddNewUser } = useLocalization();

    const { dealerDataInfo } = useSelector((state: IUser) => state);
    const brand: string = dealerDataInfo?.dealer.brand?.toLowerCase() || 'vw';

    const [selectedDealerships, setSelectedDealerships] = useState<
        Array<string>
    >([]);
    const [selectedPermissions, setSelectedPermissions] = useState<
        Set<Permissions>
    >(new Set());
    const [loading, setLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [isCountryTouched, setIsCountryTouched] = useState<boolean>(false);

    // Groups built from Permissions enum + resolved localization labels
    // Layout: 2 columns matching Figma design
    // Left column: Internal Tools (col 1), Dealer Tools (col 1)
    // Right column: Content Tools + CV Form + eDocs + Vehicle (col 2)
    const permissionGroups = useMemo(
        () => ({
            leftCol: [
                {
                    title: ismAddNewUser.PERMISSIONS_INTERNAL_TOOLS,
                    items: [
                        {
                            label: ismAddNewUser.PERMISSIONS_INTERNAL_SECURITY,
                            value: Permissions.INTERNAL_SECURITY_MAINTENANCE,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_FIELD_USER_MGMT,
                            value: Permissions.FIELD_ACCESS_MAINTENANCE,
                        },
                    ] as IPermissionCheckbox[],
                },
                {
                    title: ismAddNewUser.PERMISSIONS_DEALER_TOOLS,
                    items: [
                        {
                            label: ismAddNewUser.PERMISSIONS_DEALER_ACTIVATION,
                            value: Permissions.DEALER_ACTIVATION,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_DEALER_SECURITY,
                            value: Permissions.DEALER_SECURITY_MAINTENANCE,
                        },
                    ] as IPermissionCheckbox[],
                },
            ],
            rightCol: [
                {
                    title: ismAddNewUser.PERMISSIONS_CONTENT_TOOLS,
                    items: [
                        {
                            label: ismAddNewUser.PERMISSIONS_NEWS_STORIES,
                            value: Permissions.NEWS_STORY_MANAGEMENT,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_ONLINE_BULLETINS,
                            value: Permissions.ONLINE_BULLETINS_MAINTENANCE,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_DISTRIBUTION_RULE,
                            value: Permissions.DISTRIBUTION_RULE_MANAGEMENT,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_STATIC_CONTENT,
                            value: Permissions.CONTENT_MAINTENANCE_PERMISSION,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_CV_FORM,
                            value: Permissions.CV_FORM_MAINTENANCE,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_EDOCS,
                            value: Permissions.E_DOCUMENT_MAINTENANCE,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_VEHICLE_MAINTENANCE_PERM,
                            value: Permissions.VEHICLE_MAINTENANCE,
                        },
                    ] as IPermissionCheckbox[],
                },
            ],
        }),
        [ismAddNewUser],
    );

    const dealerOptions = [
        { value: '490000', label: '490000, 490A00 (US dealers)' },
        { value: '9000VW', label: '9000 (Canada dealers)' },
        { value: '495002', label: '495002, 495A02 (Re-marketing dealers)' },
    ];

    const dealerLabelToValue = useMemo(
        () => new Map(dealerOptions.map((o) => [o.label, o.value])),
        [],
    );
    const dealerValueToLabel = useMemo(
        () => new Map(dealerOptions.map((o) => [o.value, o.label])),
        [],
    );
    const dealerOptionLabels = dealerOptions.map((o) => o.label);

    const countryOptions = [
        { value: 'USA', label: 'USA' },
        { value: 'Canada', label: 'Canada' },
    ];
    const countryLabelToValue = useMemo(
        () => new Map(countryOptions.map((o) => [o.label, o.value])),
        [],
    );
    const countryValueToLabel = useMemo(
        () => new Map(countryOptions.map((o) => [o.value, o.label])),
        [],
    );
    const countryOptionLabels = countryOptions.map((o) => o.label);

    const selectedDealerLabel =
        selectedDealerships.length > NUMBER_ZERO
            ? dealerValueToLabel.get(selectedDealerships[0]) ??
              selectedDealerships[0]
            : '';

    const handleActiveModal = useCallback(() => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_ACTIVE,
            payload: {
                titleContent: t(ISM_ADD_NEW_USER_MODAL_TITLE),
                bodyContent: t(ISM_ADD_NEW_USER_MODAL_CONTENT),
                cancelBtnLabel: t(ISM_ADD_NEW_USER_MODAL_CANCEL),
                confirmBtnLabel: t(ISM_ADD_NEW_USER_MODAL_CONFIRM),
                onCancelHandler: cancelHandler,
                onConfirmHandler: confirmHandler,
            },
        });
    }, []);

    useEffect(() => {
        handleActiveModal();
    }, []);

    useEffect(() => {
        i18n.on('languageChanged', handleActiveModal);
        return () => {
            i18n.off('languageChanged', handleActiveModal);
        };
    }, []);

    const cancelHandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: { shouldShow: false },
        });
    };

    const confirmHandler = () => {
        dispatch({ type: MODAL_CONTROL_ACTIONS.SET_INACTIVE });
        navigate('/dashboard/systemadmin/internal-security-maintenance');
    };

    const handleBackClick = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: {
                shouldShow: true,
                onCancelHandler: cancelHandler,
                onConfirmHandler: confirmHandler,
            },
        });
    };

    const handleDealerSelect = (dealerLabel: string) => {
        const dealerId = dealerLabelToValue.get(dealerLabel) ?? dealerLabel;
        setSelectedDealerships([dealerId]);
    };

    const handlePermissionChange = (permission: Permissions) => {
        setSelectedPermissions((prev) => {
            const next = new Set(prev);
            if (next.has(permission)) {
                next.delete(permission);
            } else {
                next.add(permission);
            }
            return next;
        });
    };

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
    } = useFormik({
        initialValues: {
            country: '',
            phone: '',
            email: prefillEmail,
            firstName: '',
            lastName: '',
        },
        validationSchema: addNewUserValidations,
        onSubmit: handleFormSubmit,
    });

    const selectedCountryLabel =
        values.country !== ''
            ? countryValueToLabel.get(values.country) ?? values.country
            : '';

    const handleCountrySelect = (countryLabel: string) => {
        const countryCode = countryLabelToValue.get(countryLabel) ?? '';
        setIsCountryTouched(true);
        setFieldValue('country', countryCode);
    };

    const countryHasError = isCountryTouched && values.country === '';
    const countryErrorMessage = countryHasError
        ? t('Label.Field.Error.Required')
        : '';

    async function handleFormSubmit(): Promise<void> {
        if (
            selectedDealerships.length === NUMBER_ZERO ||
            selectedPermissions.size === NUMBER_ZERO ||
            values.country === ''
        ) {
            setIsCountryTouched(true);
            setIsError(true);
            return;
        }

        setLoading(true);
        setIsError(false);

        const primaryDealer = selectedDealerships[0];
        const permissionSet = new Set(selectedPermissions);

        if (permissionSet.has(Permissions.DEALER_SECURITY_MAINTENANCE)) {
            permissionSet.add(Permissions.USER_ADMIN);
        }

        const permissions = Array.from(permissionSet, (permission) => ({
            dealerId: null,
            permission,
        }));

        let associations = [];

        if (primaryDealer === '490000') {
            associations = [{ dealerId: '490000' }, { dealerId: '490A00' }];
        } else if (primaryDealer === '495002') {
            associations = [{ dealerId: '495002' }, { dealerId: '495A02' }];
        } else {
            associations = [{ dealerId: primaryDealer }];
        }

        try {
            const service = new DealerManagementService();
            await service.addNewUser({
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                emailAddress: values.email.toLowerCase(),
                phoneNumber: values.phone.replace(/\D/g, ''),
                isFieldAccessUser: false,
                country: values.country,
                dealerId: primaryDealer,
                permissions,
                associations: associations,
            });

            setLoading(false);
            navigate('/dashboard/systemadmin/internal-security-maintenance', {
                replace: true,
                state: {
                    addUserStatus: 'success',
                },
            });
        } catch {
            setLoading(false);
            navigate('/dashboard/systemadmin/internal-security-maintenance', {
                replace: true,
                state: {
                    addUserStatus: 'error',
                },
            });
        }
    }

    const onChangePhoneHandler = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const formattedPhone = addHyphens(event.target.value);
        setFieldValue(event.target.name, formattedPhone);
    };

    const isSubmitDisabled =
        values.country === '' ||
        values.phone === '' ||
        values.email === '' ||
        values.firstName === '' ||
        values.lastName === '' ||
        !!Object.keys(errors).length ||
        selectedDealerships.length === NUMBER_ZERO ||
        selectedPermissions.size === NUMBER_ZERO ||
        loading;

    const renderPermissionGroup = (
        groupTitle: string,
        items: IPermissionCheckbox[],
    ) => (
        <div className='ism-add-user-container__permission-group'>
            <span className='ism-add-user-container__permission-group-title'>
                {groupTitle}
            </span>
            {items.map((item) => (
                <label key={item.value} className='c-checkbox'>
                    <input
                        className='c-checkbox__input'
                        type='checkbox'
                        value={item.value}
                        checked={selectedPermissions.has(item.value)}
                        onChange={() => handlePermissionChange(item.value)}
                    />
                    <span className='c-checkbox__label'>{item.label}</span>
                </label>
            ))}
        </div>
    );

    return (
        <div className='ism-add-user-container'>
            <div className={`ism-add-user-container__header ${brand}`}>
                <div className='one'>
                    <button
                        className='c-btn c-btn--link c-btn--small c-btn--simple'
                        onClick={handleBackClick}
                    >
                        <i
                            className='c-btn__icon c-icon c-icon--[semantic-back]'
                            aria-hidden='true'
                            role='img'
                        ></i>
                        <span className='c-btn__text'>
                            {ismAddNewUser.HEADER_BACK}
                        </span>
                    </button>
                </div>
            </div>

            <div className='ism-add-user-container__body'>
                {isError && (
                    <Notification
                        content={ismAddNewUser.NOTIFICATION_ERROR}
                        type={NotificationType.ERROR}
                        visibility={isError}
                        setVisibility={setIsError}
                        withoutClose={true}
                    />
                )}

                <div className='ism-add-user-container__body-header'>
                    {ismAddNewUser.BODY_HEADER}
                </div>
                <div className='ism-add-user-container__body-allfields'>
                    <span>*</span>
                    {ismAddNewUser.BODY_ALLFIELDS}
                </div>

                <form
                    className='ism-add-user-container__body--form'
                    onSubmit={handleSubmit}
                >
                    <Input
                        labelName={ismAddNewUser.FORM_LABEL_FIRSTNAME}
                        type={'text'}
                        name={'firstName'}
                        maxlength={30}
                        handlerChange={handleChange}
                        handlerBlur={handleBlur}
                        value={values.firstName}
                        asErrors={errors.firstName}
                        asTouched={touched.firstName}
                        asSubmitError={''}
                    />
                    <Input
                        labelName={ismAddNewUser.FORM_LABEL_LASTNAME}
                        type={'text'}
                        name={'lastName'}
                        maxlength={30}
                        handlerChange={handleChange}
                        handlerBlur={handleBlur}
                        value={values.lastName}
                        asErrors={errors.lastName}
                        asTouched={touched.lastName}
                        asSubmitError={''}
                    />
                    <div className='ism-add-user-container__country-phone-row'>
                        <div className='ism-add-user-container__country-field'>
                            <label className='c-form-field__label'>
                                {t(SYSTEM_ADMIN_PAGE_COUNTRY_LABEL)}
                                <span>*</span>
                            </label>
                            <div onClick={() => setIsCountryTouched(true)}>
                                <ComboBox
                                    name={'ismCountrySelect'}
                                    options={countryOptionLabels}
                                    placeholder={''}
                                    defaultValue={''}
                                    onSelect={handleCountrySelect}
                                    selectedOption={selectedCountryLabel}
                                    multiselectable={false}
                                    disabled={false}
                                    hasError={countryHasError}
                                />
                            </div>
                            {countryErrorMessage && (
                                <span className={'c-error-message'}>
                                    {countryErrorMessage}
                                </span>
                            )}
                        </div>
                        <Input
                            labelName={ismAddNewUser.FORM_LABEL_PHONE}
                            type={'text'}
                            name={'phone'}
                            maxlength={12}
                            handlerChange={onChangePhoneHandler}
                            handlerBlur={handleBlur}
                            value={values.phone}
                            asErrors={errors.phone}
                            asTouched={touched.phone}
                            asSubmitError={''}
                            handleKeyPress={onKeyPressPhoneHandler}
                        />
                    </div>
                    <Input
                        labelName={ismAddNewUser.FORM_LABEL_EMAIL}
                        type={'text'}
                        name={'email'}
                        maxlength={50}
                        handlerChange={handleChange}
                        handlerBlur={handleBlur}
                        value={values.email}
                        asErrors={errors.email}
                        asTouched={touched.email}
                        asSubmitError={''}
                    />

                    <div className='ism-add-user-container__section-header'>
                        {ismAddNewUser.PERMISSIONS_HEADER}
                    </div>
                    <p className='ism-add-user-container__section-description'>
                        {ismAddNewUser.PERMISSIONS_DESCRIPTION}
                    </p>
                    <div className='ism-add-user-container__permissions-grid'>
                        <div className='ism-add-user-container__permissions-col'>
                            {permissionGroups.leftCol.map((group) => (
                                <React.Fragment key={group.title}>
                                    {renderPermissionGroup(
                                        group.title,
                                        group.items,
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                        <div className='ism-add-user-container__permissions-col'>
                            {permissionGroups.rightCol.map((group) => (
                                <React.Fragment key={group.title}>
                                    {renderPermissionGroup(
                                        group.title,
                                        group.items,
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    <div className='ism-add-user-container__section-header'>
                        {ismAddNewUser.DEALERSHIP_HEADER}
                    </div>
                    <p className='ism-add-user-container__section-description'>
                        {ismAddNewUser.DEALERSHIP_DESCRIPTION}
                    </p>
                    <div className='ism-add-user-container__dealership'>
                        <ComboBox
                            name={'ismDealershipSelect'}
                            options={dealerOptionLabels}
                            placeholder={
                                ismAddNewUser.DEALERSHIP_SELECT_PLACEHOLDER
                            }
                            defaultValue={''}
                            onSelect={handleDealerSelect}
                            selectedOption={selectedDealerLabel}
                            optionModifier={
                                ismAddNewUser.DEALERSHIP_SELECT_MODIFIER
                            }
                            multiselectable={false}
                            disabled={false}
                        />
                    </div>

                    <div className='ism-add-user-container__button-ct'>
                        <button
                            className={`ism-add-user--form_button secondary c-btn ${brand}`}
                            type='button'
                            onClick={handleBackClick}
                        >
                            <span className='c-btn__text'>
                                {ismAddNewUser.BUTTON_CANCEL}
                            </span>
                        </button>
                        <button
                            className={`ism-add-user--form_button c-btn ${brand}`}
                            type='submit'
                            disabled={isSubmitDisabled}
                        >
                            <span className='c-btn__text'>
                                {loading ? (
                                    <Spinner />
                                ) : (
                                    ismAddNewUser.BUTTON_SUBMIT
                                )}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ISMAddNewUser;
